import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AddressBook, CheckCircle, XCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { LogoBox, logoRequestSrc } from "@/components/buyer/BuyerLogoRequest";
import { inboxChanged } from "@/components/layout/useInboxCount";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BTN = "border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100 disabled:opacity-40";
const err = (e, f) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : f; };

// One pending "change my details" request from a buyer: approve applies it to all their pages.
export function DetailRequestRow({ row, onDone, showOpen = true }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const p = `detail-request-${row.id}`;
  const act = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/admin/buyer-detail-requests/${row.id}/${kind}`, { note: kind === "reject" ? reason.trim() || null : null });
      const mail = row.is_test ? "Test buyer, so no email is sent." : "The buyer will be emailed.";
      toast.success(kind === "approve" ? `Approved and live on all their pages. ${mail}` : `Turned down. ${mail}`);
      inboxChanged();
      onDone?.();
    } catch (e) { toast.error(err(e, "Couldn't update the request")); } finally { setBusy(false); }
  };
  return (
    <div className="border-b border-black p-3 last:border-b-0" data-testid={p}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <AddressBook size={13} weight="bold" />
        <b data-testid={`${p}-buyer`}>{row.firm_name || "Buyer"}</b>
        <span className="text-zinc-500">{row.type === "logo" ? "sent a new logo" : "asked to change their details"} · {row.submitted_by?.name || row.submitted_by?.email}</span>
        <span className="font-mono text-[10px] text-zinc-500">{new Date(row.submitted_at).toLocaleString()}</span>
        {showOpen && <Link to={`/clients/${row.org_id}/manage`} className="ml-auto text-[10px] uppercase tracking-widest underline" data-testid={`${p}-open`}>Open buyer</Link>}
      </div>
      {row.type === "logo" ? (
        <div className="mt-2 flex flex-wrap gap-4" data-testid={`${p}-logos`}>
          <LogoBox src={row.current_logo_url ? `${BACKEND}${row.current_logo_url}` : null} label="Current" testId={`${p}-logo-current`} />
          <LogoBox src={logoRequestSrc(row.id)} label="New" testId={`${p}-logo-new`} />
        </div>
      ) : (
        <ul className="mt-1.5 space-y-0.5 text-[11px]" data-testid={`${p}-changes`}>
          {Object.entries(row.changes).map(([k, c]) => (
            <li key={k}><b>{c.label}:</b> <span className="text-zinc-500 line-through">{c.before || "—"}</span> → <span className="font-semibold">{c.after || "—"}</span></li>
          ))}
        </ul>
      )}
      {row.note && <div className="mt-2 border-l-4 border-amber-500 bg-amber-50 p-2 text-xs whitespace-pre-wrap" data-testid={`${p}-note`}>{row.note}</div>}
      {rejecting ? (
        <div className="mt-2 space-y-2">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} placeholder="Short reason the buyer will see (optional)"
                    className="w-full border border-black p-2 text-xs" data-testid={`${p}-reason`} />
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => act("reject")} className={`${BTN} bg-rose-600 text-white`} data-testid={`${p}-reject-confirm`}>
              <XCircle size={12} weight="bold" /> Turn down
            </button>
            <button type="button" onClick={() => setRejecting(false)} className={`${BTN} hover:bg-black hover:text-white`} data-testid={`${p}-reject-cancel`}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => act("approve")} className={`${BTN} bg-emerald-700 text-white`} data-testid={`${p}-approve`}>
            <CheckCircle size={12} weight="bold" /> {busy ? "Applying…" : "Approve"}
          </button>
          <button type="button" disabled={busy} onClick={() => setRejecting(true)} className={`${BTN} hover:bg-black hover:text-white`} data-testid={`${p}-reject`}>
            <XCircle size={12} weight="bold" /> Turn down
          </button>
        </div>
      )}
    </div>
  );
}

export function useDetailRequests(orgId) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/buyer-detail-requests", { params: { status: "pending", ...(orgId ? { org_id: orgId } : {}) } })
      .then((r) => setRows(r.data.requests || [])).catch(() => setRows([]));
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  return [rows, load];
}

// Manage Buyer: this buyer's waiting details request, if any.
export function DetailRequestsBanner({ orgId, onDone }) {
  const [rows, load] = useDetailRequests(orgId);
  if (!rows.length) return null;
  return (
    <div className="border-2 border-amber-600 bg-amber-50" data-testid="manage-buyer-detail-requests">
      {rows.map((r) => <DetailRequestRow key={r.id} row={r} showOpen={false} onDone={() => { load(); onDone?.(); }} />)}
    </div>
  );
}
